import { useState } from 'react';
import Image from 'next/image';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import getItemInfo from '@/utils/itemInfo';
import { IBaseItem } from '@/utils/types/Items';
import cn from 'clsx';
import BackgroundWrapper from '@/components/BackgroundWrapper/BackgroundWrapper';

interface Props {
    skin: IBaseItem;
    type: 'mini' | 'big' | 'cart' | 'main';
    blurOnLoad?: boolean;
    selected?: boolean;
    className?: string;
}

const Skin = ({ type, skin, blurOnLoad, selected, className }: Props) => {
    const [isLoading, setIsLoading] = useState(true);

    const [isOpen, setIsOpen] = useState(false);

    const { name, itemName } = getItemInfo(skin);

    const component = () => {
        switch (type) {
            case 'big': {
                return (
                    <div
                        style={{
                            background: `linear-gradient(180deg, transparent 70%, ${skin.colorHex} 170%)`,
                            boxShadow: selected ? `0 0 4px #9161EF` : '',
                        }}
                        className={'w-28 pb-12 px-3 m-1 relative rounded '}
                    >
                        <span
                            style={{ backgroundColor: skin.colorHex, marginInline: 'auto' }}
                            className={'w-8 h-1 rounded absolute right-0 left-0 bottom-[-2px]'}
                        />
                        <div className={'rounded flex items-center justify-center shrink-0 pt-0.5'}>
                            <BackgroundWrapper
                                height={86}
                                iconBackground={skin.iconBackground}
                                width={86}
                            >
                                <Image
                                    className={
                                        blurOnLoad
                                            ? cn(
                                                  'duration-700 ease-in-out',
                                                  isLoading
                                                      ? 'grayscale blur-2xl scale-110'
                                                      : 'grayscale-0 blur-0 scale-100'
                                              )
                                            : ''
                                    }
                                    onLoad={() => setIsLoading(false)}
                                    id={'skin'}
                                    src={skin.icon + '/86fx86'}
                                    alt={skin.name}
                                    width={86}
                                    height={86}
                                />
                            </BackgroundWrapper>
                            <div className={'absolute bottom-1 text-sm w-full px-2 text-center'}>
                                <p className={'text-yellow'}>${skin.priceUsd}</p>
                                <p className={'text-white overflow-hidden overflow-ellipsis'}>
                                    {skin.name}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            }
            case 'main': {
                return (
                    <div
                        style={{
                            background: `linear-gradient(180deg, transparent 70%, ${skin.colorHex} 170%)`,
                            boxShadow: selected ? `0 0 4px #9161EF` : '',
                        }}
                        className={'w-full pb-10 px-3 m-1 relative rounded '}
                    >
                        <span
                            style={{ backgroundColor: skin.colorHex, marginInline: 'auto' }}
                            className={'w-8 h-1 rounded absolute right-0 left-0 bottom-[-2px]'}
                        />
                        <div className={'rounded flex items-center justify-center shrink-0 pt-0.5'}>
                            <BackgroundWrapper
                                height={86}
                                iconBackground={skin.iconBackground}
                                width={86}
                            >
                                <Image
                                    className={
                                        blurOnLoad
                                            ? cn(
                                                  'duration-700 ease-in-out',
                                                  isLoading
                                                      ? 'grayscale blur-2xl scale-110'
                                                      : 'grayscale-0 blur-0 scale-100'
                                              )
                                            : ''
                                    }
                                    onLoad={() => setIsLoading(false)}
                                    id={'skin'}
                                    src={skin.icon + '/86fx86'}
                                    alt={skin.name}
                                    width={86}
                                    height={86}
                                />
                            </BackgroundWrapper>
                            <div className={'absolute bottom-1 text-sm w-full px-2 text-center'}>
                                <p className={'text-yellow'}>${skin.priceUsd}</p>
                                <p className={'text-white overflow-hidden overflow-ellipsis'}>
                                    {skin.name}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            }
            case 'mini': {
                return (
                    <div
                        style={{
                            background: `linear-gradient(180deg, transparent 70%, ${skin.colorHex} 170%)`,
                        }}
                        className={'w-19 h-19 relative rounded flex justify-center items-center'}
                    >
                        <span
                            style={{ backgroundColor: skin.colorHex }}
                            className={'w-8 h-1 rounded absolute left-[20px] bottom-[-1px]'}
                        />
                        <div className={'rounded flex items-center shrink-0 pt-0.5'}>
                            <BackgroundWrapper
                                height={60}
                                iconBackground={skin.iconBackground}
                                width={60}
                            >
                                <Image
                                    className={
                                        blurOnLoad
                                            ? cn(
                                                  'duration-700 ease-in-out',
                                                  isLoading
                                                      ? 'grayscale blur-2xl scale-110'
                                                      : 'grayscale-0 blur-0 scale-100'
                                              )
                                            : ''
                                    }
                                    onLoad={() => setIsLoading(false)}
                                    id={'skin'}
                                    src={skin.icon + '/60fx60'}
                                    alt={skin.name}
                                    width={60}
                                    height={60}
                                />
                            </BackgroundWrapper>
                        </div>
                    </div>
                );
            }
            case 'cart': {
                return (
                    <div
                        style={{
                            background: `linear-gradient(180deg, transparent 70%, ${skin.colorHex} 170%)`,
                            boxShadow: selected ? `0 0 4px #9161EF` : '',
                        }}
                        className={
                            'w-19 h-28 m-1 relative rounded flex justify-center items-center'
                        }
                    >
                        <span
                            style={{ backgroundColor: skin.colorHex }}
                            className={'w-8 h-1 rounded absolute left-[22px] bottom-[-1px]'}
                        />
                        <div className={'rounded flex items-center shrink-0 pt-0.5 pb-3'}>
                            <BackgroundWrapper
                                height={60}
                                iconBackground={skin.iconBackground}
                                width={60}
                            >
                                <Image
                                    className={
                                        blurOnLoad
                                            ? cn(
                                                  'duration-700 ease-in-out',
                                                  isLoading
                                                      ? 'grayscale blur-2xl scale-110'
                                                      : 'grayscale-0 blur-0 scale-100'
                                              )
                                            : ''
                                    }
                                    onLoad={() => setIsLoading(false)}
                                    id={'skin'}
                                    src={skin.icon + '/60fx60'}
                                    alt={skin.name}
                                    width={60}
                                    height={60}
                                />
                            </BackgroundWrapper>
                            <p className={'text-yellow absolute bottom-1 left-3'}>
                                ${skin.priceUsd}
                            </p>
                        </div>
                    </div>
                );
            }
        }
    };

    return (
        <Popover
            isOpen={type != 'big' ? isOpen : false}
            content={({ position, childRect, popoverRect }) => (
                <ArrowContainer // if you'd like an arrow, you can import the ArrowContainer!
                    position={position}
                    childRect={childRect}
                    popoverRect={popoverRect}
                    arrowColor={'#32353D'}
                    arrowSize={10}
                    arrowStyle={{ opacity: 0.7 }}
                    className={''}
                    arrowClassName={'mb-1'}
                >
                    <div className={'w-fit shadow-main'}>
                        <div
                            className={'rounded-lg'}
                            style={{
                                background: '#2D3032',
                                boxShadow: `0px 50px 50px -60px ${skin.colorHex} inset`,
                            }}
                        >
                            <div
                                className={'rounded w-16 mx-auto'}
                                style={{ height: 5, background: skin.colorHex }}
                            />
                            <BackgroundWrapper
                                height={120}
                                iconBackground={skin.iconBackground}
                                width={160}
                            >
                                <Image
                                    className="w-40 h-auto mx-auto py-2"
                                    src={skin.icon + '/160fx120'}
                                    width={160}
                                    height={120}
                                    alt={skin.name}
                                />
                            </BackgroundWrapper>
                            <div
                                className={
                                    'text-sm font-bold p-3 overflow-hidden w-40 whitespace-normal'
                                }
                            >
                                <p className={'text-yellow'}>${skin.priceUsd}</p>
                                <p>{name}</p>
                                <p className={'text-xs text-text_gray'}>{itemName}</p>
                            </div>
                        </div>
                    </div>
                </ArrowContainer>
            )}
        >
            <div
                onMouseLeave={() => setIsOpen(false)}
                onMouseEnter={() => setIsOpen(true)}
                className={className || ''}
            >
                {component()}
            </div>
        </Popover>
    );
};

export default Skin;
